import { env } from '@/lib/env';
import { 
  WebSocketMessage, 
  WebSocketMessageTypes,
  WebSocketConnectionStatus,
  SocketMessageData
} from '@/lib/types/socket';

// Check if we're in production environment
const isProduction = process.env.NODE_ENV === 'production';

// Check if we're on Netlify where WebSockets may not work properly
const isNetlifyEnvironment = () => {
  return (
    typeof window !== 'undefined' && 
    (window.location.hostname.includes('netlify.app') || 
     document.referrer.includes('netlify.app') ||
     // Additional checks for Netlify environments
     window.location.hostname.includes('netlify') ||
     // Check for Netlify environment variables that might be exposed
     (window as any).NETLIFY !== undefined ||
     process.env.NETLIFY === 'true')
  );
};

// Fallback mechanism for environments where WebSockets don't work
class WebSocketFallback {
  private callbacks: Record<string, Array<(data: any) => void>> = {
    message: [],
    connection: []
  };
  
  // Simulate connection status
  public onopen() {
    this.notifyConnectionCallbacks(true);
  }
  
  // Register message handlers
  public onMessage(callback: (data: SocketMessageData) => void) {
    this.callbacks.message.push(callback);
    return () => {
      this.callbacks.message = this.callbacks.message.filter(cb => cb !== callback);
    };
  }
  
  // Register connection status handlers
  public onConnectionChange(callback: (connected: boolean) => void) {
    this.callbacks.connection.push(callback);
    // Immediately notify as connected to prevent loading issues
    setTimeout(() => callback(true), 100);
    return () => {
      this.callbacks.connection = this.callbacks.connection.filter(cb => cb !== callback);
    };
  }
  
  // Notify all connection callbacks
  private notifyConnectionCallbacks(status: boolean) {
    this.callbacks.connection.forEach(callback => callback(status));
  }
  
  // Simulate sending a message (will be a no-op in production)
  public sendMessage(message: WebSocketMessage) {
    if (!isProduction) {
      console.log('WebSocket fallback: Message would be sent', message);
    }
    
    // For certain message types, we can simulate responses
    if (message.type === WebSocketMessageTypes.PING) {
      this.simulatePingResponse();
    }
  }
  
  // Simulate a ping response to keep the application functioning
  private simulatePingResponse() {
    setTimeout(() => {
      const pingResponse: SocketMessageData = {
        type: WebSocketMessageTypes.PONG,
        message: { timestamp: new Date().toISOString() }
      };
      
      this.callbacks.message.forEach(callback => callback(pingResponse));
    }, 50);
  }
  
  // Get connection status (always return CONNECTED for fallback)
  public getConnectionStatus() {
    return WebSocketConnectionStatus.CONNECTED;
  }
  
  // No-op for disconnect
  public disconnect() {}
}

// Custom error handler that suppresses WebSocket errors in production
const handleWebSocketError = (error: Error | Event | string, errorType = 'Error') => {
  // Only log detailed errors in development
  if (!isProduction) {
    console.error(`WebSocket ${errorType}:`, error);
  } else {
    // In production, we only log to the console if it's not a connection error
    if (errorType !== 'Connection Error') {
      console.warn(`WebSocket ${errorType} occurred in production`);
    }
  }
  
  // Prevent errors from being shown in the console in production
  // This is key to hiding the error messages on Netlify
  if (isProduction) {
    return true; // Prevents the error from propagating
  }
  return false;
};

class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private fallback: WebSocketFallback | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = isProduction ? 2 : 5; // Fewer attempts in production
  private reconnectTimeout = isProduction ? 5000 : 3000; // Longer timeout in production
  private messageCallbacks: Array<(data: SocketMessageData) => void> = [];
  private connectionCallbacks: Array<(connected: boolean) => void> = [];
  private connectionStatus: WebSocketConnectionStatus = WebSocketConnectionStatus.DISCONNECTED;
  private skipReconnect = false;
  private usingFallback = false;

  private constructor() {
    // Check if we're in a Netlify environment
    if (isNetlifyEnvironment()) {
      this.initializeFallback();
      return;
    }
    
    // Try to connect with real WebSockets
    this.connect();
    
    // Add global error handlers to suppress WebSocket errors in console
    if (isProduction) {
      this.setupGlobalErrorHandling();
    }
  }
  
  private initializeFallback() {
    this.usingFallback = true;
    this.skipReconnect = true;
    this.fallback = new WebSocketFallback();
    this.connectionStatus = WebSocketConnectionStatus.CONNECTED;
    
    if (!isProduction) {
      console.warn('Netlify environment detected - Using WebSocket fallback');
    }
    
    // Simulate connection after a short delay
    setTimeout(() => {
      this.fallback?.onopen();
      this.notifyConnectionChange(true);
    }, 300);
  }

  private setupGlobalErrorHandling() {
    // Override the default error handling behavior in production
    window.addEventListener('error', (event) => {
      if (event.message && (
          event.message.includes('WebSocket') ||
          event.message.includes('socket') ||
          event.message.includes('net::') ||
          event.message.includes('connection')
        )) {
        // Prevent WebSocket errors from showing in the console
        event.preventDefault();
        return false;
      }
      return true;
    }, true);
    
    // Also catch unhandled promise rejections related to WebSockets
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && typeof event.reason.message === 'string' && (
          event.reason.message.includes('WebSocket') ||
          event.reason.message.includes('socket') ||
          event.reason.message.includes('connection')
        )) {
        // Prevent WebSocket promise rejection errors from showing
        event.preventDefault();
        return false;
      }
      return true;
    });
  }

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  private connect() {
    // Skip connection attempts if we're on Netlify or at max reconnect attempts
    if (this.skipReconnect || this.ws?.readyState === WebSocket.OPEN) return;

    try {
      this.connectionStatus = WebSocketConnectionStatus.CONNECTING;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      this.ws = new WebSocket(`${protocol}//${host}/ws`);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.connectionStatus = WebSocketConnectionStatus.CONNECTED;
        this.notifyConnectionChange(true);
        if (!isProduction) {
          console.log('WebSocket connected');
        }
      };

      this.ws.onclose = () => {
        this.connectionStatus = WebSocketConnectionStatus.DISCONNECTED;
        this.notifyConnectionChange(false);
        
        // If we're in a Netlify environment and connection fails, switch to fallback
        if (isNetlifyEnvironment()) {
          this.initializeFallback();
          return;
        }
        
        // Don't attempt reconnection in production for Netlify
        if (isProduction && isNetlifyEnvironment()) {
          this.skipReconnect = true;
          this.connectionStatus = WebSocketConnectionStatus.FAILED;
          return;
        }
        
        this.handleReconnect();
      };

      this.ws.onerror = (error: Event) => {
        // Use custom error handler to prevent console errors in production
        handleWebSocketError(error, 'Connection Error');
        
        // If we're in a Netlify environment and connection fails, switch to fallback
        if (isNetlifyEnvironment()) {
          this.initializeFallback();
          return;
        }
        
        // In production on Netlify, don't try to reconnect after errors
        if (isProduction && isNetlifyEnvironment()) {
          this.skipReconnect = true;
          this.connectionStatus = WebSocketConnectionStatus.FAILED;
        }
        
        this.ws?.close();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data) as SocketMessageData;
          this.messageCallbacks.forEach(callback => callback(data));
        } catch (error) {
          handleWebSocketError(error as Error, 'Message Parse Error');
        }
      };
    } catch (error) {
      handleWebSocketError(error as Error, 'Setup Error');
      this.connectionStatus = WebSocketConnectionStatus.FAILED;
      
      // If connection setup fails, try the fallback
      if (isNetlifyEnvironment()) {
        this.initializeFallback();
      }
    }
  }

  private handleReconnect() {
    if (this.skipReconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Don't log in production to avoid console errors
      if (!isProduction) {
        console.error('Max reconnection attempts reached');
      }
      this.connectionStatus = WebSocketConnectionStatus.FAILED;
      
      // If we're in a Netlify environment and reconnection fails, switch to fallback
      if (isNetlifyEnvironment()) {
        this.initializeFallback();
      }
      return;
    }

    this.connectionStatus = WebSocketConnectionStatus.RECONNECTING;
    this.reconnectAttempts++;
    
    // Only log in development
    if (!isProduction) {
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    }
    
    setTimeout(() => this.connect(), this.reconnectTimeout);
  }

  private notifyConnectionChange(connected: boolean) {
    this.connectionCallbacks.forEach(callback => callback(connected));
  }

  onMessage(callback: (data: SocketMessageData) => void): () => void {
    if (this.usingFallback && this.fallback) {
      return this.fallback.onMessage(callback);
    }
    
    this.messageCallbacks.push(callback);
    return () => {
      this.messageCallbacks = this.messageCallbacks.filter(cb => cb !== callback);
    };
  }

  onConnectionChange(callback: (connected: boolean) => void): () => void {
    if (this.usingFallback && this.fallback) {
      return this.fallback.onConnectionChange(callback);
    }
    
    this.connectionCallbacks.push(callback);
    // Immediately notify of current status
    callback(this.connectionStatus === WebSocketConnectionStatus.CONNECTED);
    return () => {
      this.connectionCallbacks = this.connectionCallbacks.filter(cb => cb !== callback);
    };
  }

  getConnectionStatus(): WebSocketConnectionStatus {
    if (this.usingFallback && this.fallback) {
      return this.fallback.getConnectionStatus();
    }
    return this.connectionStatus;
  }

  sendMessage(message: WebSocketMessage) {
    // If using fallback, delegate to it
    if (this.usingFallback && this.fallback) {
      this.fallback.sendMessage(message);
      return;
    }
    
    // If we're in production on Netlify, silently fail but don't error
    if (isProduction && isNetlifyEnvironment()) {
      return;
    }
    
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
      } catch (error) {
        handleWebSocketError(error as Error, 'Send Error');
      }
    } else if (!isProduction) {
      // Only log in development
      console.warn('WebSocket is not connected');
    }
  }

  disconnect() {
    if (this.usingFallback && this.fallback) {
      this.fallback.disconnect();
      return;
    }
    
    try {
      this.ws?.close();
    } catch (error) {
      handleWebSocketError(error as Error, 'Disconnect Error');
    } finally {
      this.ws = null;
      this.messageCallbacks = [];
      this.connectionCallbacks = [];
      this.connectionStatus = WebSocketConnectionStatus.DISCONNECTED;
    }
  }
}

export const websocketService = WebSocketService.getInstance();